<script setup>
import {ref, onMounted, onUnmounted, computed} from 'vue';
import axios from 'axios';
import {useConfigurateurStore} from "../stores/configurateur";
import Logger from "../utils/logger";

const logger = new Logger('planning')
const configurateurStore = useConfigurateurStore();

// Référence à l'élément du calendrier
const props = defineProps({
    timezone: {type: String, default: 'Europe/Paris'},
});
const bookingToday = () => new Intl.DateTimeFormat('en-CA', {
    timeZone: props.timezone, year: 'numeric', month: '2-digit', day: '2-digit',
}).format(new Date());
const currentDate = ref(new Date());
const selectedDate = ref(null);
const hoveredCircuit = ref(null);
const selectedCar = ref(null);
const showCarModal = ref(false);
const currentCircuit = ref(null);

// Nouvelles données pour le filtrage par circuit
const availableCircuits = ref([]);
const selectedCircuitId = ref(null);
const circuitsData = ref([]);

// Données pour le regroupement Paul Ricard
const paulRicardCircuits = ref([]);
const otherCircuits = ref([]);
const selectedMainTab = ref('all'); // 'all', 'paul-ricard', ou 'circuit-id'
const selectedPaulRicardTab = ref(null);

// Fonctions pour le modal de sélection de voitures
const toggleCarMenu = (circuit) => {
    currentCircuit.value = circuit;
    showCarModal.value = true;
};

const selectCar = (car, circuitId) => {
    logger.log(`Voiture sélectionnée: ${car.name} pour le circuit ID: ${circuitId}`);
    selectedCar.value = car;
    hoveredCircuit.value = null;
    showCarModal.value = false;

    // Ici vous pourriez ajouter du code pour traiter la sélection de la voiture
    // Par exemple, envoyer une requête au serveur, mettre à jour l'état, etc.
};

const closeCarModal = () => {
    showCarModal.value = false;
};

// Fonction pour filtrer par circuit
const filterByCircuit = async (circuitId) => {
    selectedCircuitId.value = circuitId;
    await fetchCircuitData();
};

// Le premier jour de la semaine (0: dimanche, 1: lundi)
const firstDayOfWeek = 1;

// Noms des jours de la semaine
const weekdays = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
// Noms des mois
const months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

// Données calculées pour le calendrier
const calendarData = computed(() => {
    // Obtenir le premier jour du mois
    const firstDay = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth(), 1);
    // Obtenir le dernier jour du mois
    const lastDay = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth() + 1, 0);
    // Nombre de jours dans le mois
    const daysInMonth = lastDay.getDate();

    // Ajuster le premier jour de la semaine (0: dimanche, 1: lundi)
    let startingDay = firstDay.getDay() - firstDayOfWeek;
    if (startingDay < 0) startingDay += 7;

    // Créer les en-têtes pour les jours de la semaine
    const headers = [];
    for (let i = 0; i < 7; i++) {
        const dayIndex = (firstDayOfWeek + i) % 7;
        headers.push({
            text: weekdays[dayIndex].substring(0, 3),
            index: dayIndex
        });
    }

    // Créer les données pour les jours du mois
    const weeks = [];
    let day = 1;

    // Créer 6 rangées maximum pour couvrir tous les mois
    for (let i = 0; i < 6; i++) {
        const week = [];

        // Créer 7 cellules pour chaque jour de la semaine
        for (let j = 0; j < 7; j++) {
            if ((i === 0 && j < startingDay) || day > daysInMonth) {
                // Jours qui n'appartiennent pas au mois actuel
                week.push({
                    day: null,
                    isInactive: true
                });
            } else {
                // Jours du mois actuel
                const currentDateValue = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth(), day);
                const today = new Date();

                // Vérifier si des circuits sont disponibles pour cette date
                const dateString = `${currentDate.value.getFullYear()}-${String(currentDate.value.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const dayCircuits = circuitsData.value.find(item => item.date === dateString);

                week.push({
                    day,
                    date: currentDateValue,
                    isSelected: selectedDate.value &&
                        currentDateValue.getDate() === selectedDate.value.getDate() &&
                        currentDateValue.getMonth() === selectedDate.value.getMonth() &&
                        currentDateValue.getFullYear() === selectedDate.value.getFullYear(),
                    isToday: currentDateValue.getDate() === today.getDate() &&
                        currentDateValue.getMonth() === today.getMonth() &&
                        currentDateValue.getFullYear() === today.getFullYear(),
                    circuits: dateString >= bookingToday() && dayCircuits ? dayCircuits.circuits : []
                });

                day++;
            }
        }

        weeks.push(week);

        // Sortir de la boucle si tous les jours du mois ont été affichés
        if (day > daysInMonth) break;
    }

    return {
        headers,
        weeks
    };
});


// Fonction pour sélectionner une date
const selectDate = (date) => {
    selectedDate.value = date;
};

// Navigation dans le calendrier
const prevMonth = () => {
    currentDate.value = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth() - 1, 1);
};

const nextMonth = () => {
    currentDate.value = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth() + 1, 1);
};

const goToToday = () => {
    currentDate.value = new Date();
};

// Fonction pour séparer les circuits Paul Ricard des autres
const separateCircuits = () => {
    if (!availableCircuits.value || availableCircuits.value.length === 0) return;

    paulRicardCircuits.value = availableCircuits.value.filter(circuit => {
        const name = circuit.name || '';
        const designation = circuit.designation || '';
        return name.toLowerCase().includes('paul ricard') || designation.toLowerCase().includes('paul ricard');
    });

    otherCircuits.value = availableCircuits.value.filter(circuit => {
        const name = circuit.name || '';
        const designation = circuit.designation || '';
        return !name.toLowerCase().includes('paul ricard') && !designation.toLowerCase().includes('paul ricard');
    });

    logger.log('Paul Ricard circuits:', paulRicardCircuits.value);
    logger.log('Other circuits:', otherCircuits.value);

    // Initialiser le premier onglet Paul Ricard si disponible
    if (paulRicardCircuits.value.length > 0 && !selectedPaulRicardTab.value) {
        selectedPaulRicardTab.value = paulRicardCircuits.value[0].id;
    }
};

// Fonctions de navigation pour les onglets
const selectMainTab = (tab) => {
    selectedMainTab.value = tab;
    if (tab === 'paul-ricard' && paulRicardCircuits.value.length > 0) {
        selectedPaulRicardTab.value = paulRicardCircuits.value[0].id;
        filterByCircuit(paulRicardCircuits.value[0].id);
    } else if (tab === 'all') {
        filterByCircuit(null);
    } else {
        // C'est un circuit spécifique
        filterByCircuit(parseInt(tab));
    }
};

const selectPaulRicardTab = (circuitId) => {
    selectedPaulRicardTab.value = circuitId;
    filterByCircuit(circuitId);
};

// Fonction pour simuler une requête AJAX
const fetchCircuitData = async () => {
    logger.log('Fetching circuit data...');

    // Construire l'URL avec le filtre de circuit si défini
    let url = '/api/planning';
    if (selectedCircuitId.value) {
        url += `?circuit_id=${selectedCircuitId.value}`;
    }

    let response = await axios.get(url);
    circuitsData.value = response.data.data;
    availableCircuits.value = response.data.circuits;

    // Séparer les circuits après avoir récupéré les données
    separateCircuits();
};

// Initialisation au montage du composant
onMounted(() => {
    fetchCircuitData();
});


const handleClickVoiture = (circuit, voiture) => {
    configurateurStore.setLoaderPlanningFront(true)
    configurateurStore.setCircuitId(circuit.id)
    configurateurStore.setCircuitDateId(circuit.circuit_date_id)
    configurateurStore.setVoitureId(voiture.id)
    configurateurStore.setStageId(voiture.stage_id)
    configurateurStore.setStep('voiture')
    configurateurStore.openModal()
}

</script>

<template>
    <!-- Nav Pills pour filtrage par circuit -->
    <div class="container mb-4" v-if="availableCircuits && availableCircuits.length > 0">
        <!-- Dropdown mobile -->
        <div class="dropdown d-lg-none mb-3">
            <button class="btn btn-outline-secondary dropdown-toggle w-100" type="button" id="circuitPlanningDropdown"
                    data-bs-toggle="dropdown" aria-expanded="false">
                {{
                    selectedCircuitId ? (availableCircuits.find(c => c.id === selectedCircuitId)?.designation || availableCircuits.find(c => c.id === selectedCircuitId)?.name || 'Circuit sélectionné') : 'Tous les circuits'
                }}
            </button>
            <ul class="dropdown-menu w-100" aria-labelledby="circuitPlanningDropdown">
                <li>
                    <a class="dropdown-item" :class="{ active: selectedCircuitId === null }"
                       href="#" @click.prevent="filterByCircuit(null)">Tous les circuits</a>
                </li>
                <!-- Groupe Paul Ricard avec sous-éléments si on en a plusieurs -->
                <template v-if="paulRicardCircuits.length > 1">
                    <li>
                        <span class="dropdown-item disabled fw-bold text-uppercase bg-light">
                            Circuit Paul Ricard
                        </span>
                    </li>
                    <li v-for="circuit in paulRicardCircuits" :key="`pr-${circuit.id}`">
                        <a class="dropdown-item ps-4" :class="{ active: selectedCircuitId === circuit.id }"
                           href="#" @click.prevent="filterByCircuit(circuit.id)">{{
                                circuit.designation || circuit.name
                            }}</a>
                    </li>
                    <li v-if="otherCircuits.length > 0">
                        <hr class="dropdown-divider">
                    </li>
                    <li v-for="circuit in otherCircuits" :key="`other-${circuit.id}`">
                        <a class="dropdown-item" :class="{ active: selectedCircuitId === circuit.id }"
                           href="#" @click.prevent="filterByCircuit(circuit.id)">{{
                                circuit.designation || circuit.name
                            }}</a>
                    </li>
                </template>
                <!-- Tous les circuits si pas de regroupement -->
                <template v-else>
                    <li v-for="circuit in availableCircuits" :key="circuit.id">
                        <a class="dropdown-item" :class="{ active: selectedCircuitId === circuit.id }"
                           href="#" @click.prevent="filterByCircuit(circuit.id)">{{
                                circuit.designation || circuit.name
                            }}</a>
                    </li>
                </template>
            </ul>
        </div>

        <!-- Pills desktop - Structure à deux niveaux comme home.blade.php -->
        <div class="d-none d-lg-block">
            <!-- Onglets principaux -->
            <ul class="nav nav-pills mt-3 justify-content-center" id="circuit-planning-pills" role="tablist">
                <li class="nav-item" role="presentation">
                    <button class="nav-link" :class="{ active: selectedMainTab === 'all' }"
                            @click="selectMainTab('all')" type="button">
                        Tous les circuits
                    </button>
                </li>
                <li class="nav-item" role="presentation" v-if="paulRicardCircuits.length > 1">
                    <button class="nav-link" :class="{ active: selectedMainTab === 'paul-ricard' }"
                            @click="selectMainTab('paul-ricard')" type="button">
                        Circuit Paul Ricard
                    </button>
                </li>
                <li class="nav-item" role="presentation" v-for="circuit in otherCircuits" :key="`main-${circuit.id}`">
                    <button class="nav-link" :class="{ active: selectedMainTab === circuit.id.toString() }"
                            @click="selectMainTab(circuit.id.toString())" type="button">
                        {{ circuit.designation || circuit.name }}
                    </button>
                </li>
                <!-- Paul Ricard individuels si un seul -->
                <template v-if="paulRicardCircuits.length === 1">
                    <li class="nav-item" role="presentation" v-for="circuit in paulRicardCircuits"
                        :key="`main-single-pr-${circuit.id}`">
                        <button class="nav-link" :class="{ active: selectedMainTab === circuit.id.toString() }"
                                @click="selectMainTab(circuit.id.toString())" type="button">
                            {{ circuit.designation || circuit.name }}
                        </button>
                    </li>
                </template>
            </ul>

            <!-- Sous-onglets pour Paul Ricard -->
            <ul class="nav nav-pills mb-3 justify-content-center"
                v-if="selectedMainTab === 'paul-ricard' && paulRicardCircuits.length > 1"
                id="paul-ricard-sub-pills" role="tablist">
                <li class="nav-item" role="presentation" v-for="circuit in paulRicardCircuits"
                    :key="`sub-${circuit.id}`">
                    <button class="nav-link" :class="{ active: selectedPaulRicardTab === circuit.id }"
                            @click="selectPaulRicardTab(circuit.id)" type="button">
                        {{ circuit.designation || circuit.name }}
                    </button>
                </li>
            </ul>
        </div>
    </div>

    <div class="container calendar-container">
        <!-- En-tête du calendrier -->
        <div class="d-flex justify-content-between align-items-center mb-4">
            <h2 class="calendar-title mb-0">
                {{ months[currentDate.getMonth()] }} {{ currentDate.getFullYear() }}
            </h2>
            <div class="btn-group">
                <button @click="prevMonth" class="btn btn-outline-primary">
                    <i class="bi bi-chevron-left"></i>
                </button>
                <button @click="goToToday" class="btn btn-outline-primary">
                    Aujourd'hui
                </button>
                <button @click="nextMonth" class="btn btn-outline-primary">
                    <i class="bi bi-chevron-right"></i>
                </button>
            </div>
        </div>

        <!-- Calendrier -->
        <div class="calendar mb-4">
            <!-- En-tête des jours de la semaine -->
            <div class="row text-center fw-bold mb-2">
                <div v-for="header in calendarData.headers" :key="header.index" class="col calendar-day-header">
                    {{ header.text }}
                </div>
            </div>

            <!-- Corps du calendrier -->
            <div class="calendar-body">
                <div v-for="(week, weekIndex) in calendarData.weeks" :key="weekIndex" class="row mb-2">
                    <div v-for="(day, dayIndex) in week" :key="`${weekIndex}-${dayIndex}`"
                         :class="['col', 'position-relative', 'calendar-day', { 'calendar-day-inactive': day.isInactive }]">

                        <!-- Jour du mois -->
                        <div v-if="day.day !== null"
                             :class="['calendar-date', 'd-flex', 'justify-content-center', 'align-items-center', 'rounded-circle', 
                                     { 'selected bg-primary text-white': day.isSelected },
                                     { 'today bg-light': day.isToday }]"
                        >
                            {{ day.day }}
                        </div>

                        <!-- Circuits disponibles pour ce jour -->
                        <div v-if="day.circuits && day.circuits.length > 0" class="calendar-circuits mt-1">
                            <div v-for="circuit in day.circuits" :key="circuit.id"
                                 :style="`background-color: ${circuit.couleur}!important`"
                                 v-open-configurateur.circuit="{ id: circuit.id, selectedDateIso: `${day.date.getFullYear()}-${String(day.date.getMonth() + 1).padStart(2, '0')}-${String(day.date.getDate()).padStart(2, '0')}`, from: 'circuit' }"
                                 class="calendar-circuit bg-primary text-white px-1 rounded small mb-1 text-truncate calendar-circuit-name"
                            >{{ circuit.name.replaceAll('-', '\n') }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Modal pour la sélection de voiture -->
    <div v-if="showCarModal" class="car-selection-modal">
        <div class="car-modal-backdrop" @click="closeCarModal"></div>
        <div class="car-modal-content">
            <div class="car-modal-header">
                <h3>Choisir une voiture</h3>
                <button class="car-modal-close" @click="closeCarModal">&times;</button>
            </div>
            <div class="car-modal-body">

                <div v-if="configurateurStore.loaderPlanningFront" class="spinner-backdrop">
                    <div class="d-flex justify-content-center">
                        <div class="spinner-border text-primary spinner-border-lg" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </div>
                <div v-if="currentCircuit" class="car-list">
                    <div>
                        <div class="car-grid">

                            <div
                                v-for="car in currentCircuit.cars"
                                :key="car.id"
                                class="card card-product text-decoration-none"
                                @click.prevent="handleClickVoiture(currentCircuit, car)"
                            >
                                <picture>
                                    <source
                                        type="image/jpeg"
                                        :srcset="`${car.image} 480w,
                                        ${car.image} 768w,
                                        ${car.image} 1024w,
                                        ${car.image} 2x`"
                                        sizes="(max-width: 480px) 480w, (max-width: 768px) 768w, 1024w">
                                    <img :src="car.image" :alt="car.name" class="card-img my-3">
                                </picture>
                                <div class="card-title">
                                    <h3 class="h5 text-black text-uppercase">{{ car.name }}</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style>
/* Affiche \n comme <br> pour les noms de circuit contenant '-', sans v-html */
.calendar-circuit-name {
    white-space: pre-line;
}

/* Style global du calendrier */
.calendar-container {
    padding: 2rem;
    background-color: #fff;
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
    margin: 0 5rem;
    transition: all 0.3s ease;
    border: 1px solid rgba(255, 255, 255, 0.18);
}

.calendar-container:hover {
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
    transform: translateY(-5px);
}

.calendar-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #333;
    letter-spacing: -0.5px;
    margin-bottom: 1rem;
}

.btn-group {
    border-radius: 50px;
    overflow: hidden;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.04);
}

.btn-group .btn {
    border: none;
    padding: 0.6rem 1.2rem;
    margin: 0 2px;
    background-color: #f8f9fa;
    color: #444;
    font-weight: 500;
    transition: all 0.2s ease;
    border-radius: 50px;
}

.btn-group .btn:hover {
    background-color: #e9ecef;
    color: #222;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 123, 255, 0.15);
}

.btn-group .btn:focus {
    box-shadow: none;
}

.calendar {
    margin-top: 1.5rem;
}

/* En-têtes des jours de la semaine */
.calendar-day-header {
    font-weight: 600;
    padding: 12px 0;
    color: #495057;
    letter-spacing: 0.5px;
    font-size: 0.9rem;
    text-transform: uppercase;
    text-align: center;
}

/* Structure du calendrier */
.calendar-body .row {
    margin-bottom: 0 !important;
}

.calendar-body {
    animation: fadeIn 0.4s ease-out;
}

/* Cellules des jours */
.calendar-day {
    min-height: 120px;
    padding: 8px;
    border: 2px solid #dee2e6;
    border-radius: 12px;
    margin: 3px;
    background-color: #fff;
    transition: all 0.3s ease;
    overflow: hidden;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
    position: relative;
}

.calendar-day:hover {
    background-color: #f9f9f9;
    transform: scale(1.02);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
    z-index: 1;
}

.calendar-day-inactive {
    background-color: #f9f9f9;
    opacity: 0.7;
    box-shadow: none;
}

.calendar-day-inactive:hover {
    transform: none;
    box-shadow: none;
}

/* Dates */
.calendar-date {
    width: 38px;
    height: 38px;
    display: flex !important;
    align-items: center;
    justify-content: center;
    transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    margin-bottom: 5px;
    font-weight: 500;
    font-size: 0.95rem;
    color: #333;
    border-radius: 50%;
    position: relative;
    border: 2px solid #c8d1db;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

/*.calendar-date:hover {
    background-color: rgba(0, 123, 255, 0.1);
    color: #0d6efd;
    transform: scale(1.1);
}*/

.calendar-date.selected {
    background-color: #0d6efd;
    color: white;
    font-weight: 600;
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
    border: 2px solid #ffffff;
}

.calendar-date.today {
    color: #0d6efd;
    font-weight: 600;
    position: relative;
    border: 2px solid #0d6efd;
    background-color: rgba(13, 110, 253, 0.1);
}

/*.calendar-date.today:after {
    content: '';
    position: absolute;
    bottom: 3px;
    left: 50%;
    transform: translateX(-50%);
    width: 6px;
    height: 6px;
    background-color: #0d6efd;
    border-radius: 50%;
    box-shadow: 0 0 4px rgba(13, 110, 253, 0.5);
}*/

/* Circuits */
.calendar-circuits {
    margin-top: 8px;
    overflow: hidden;
}

.calendar-circuit {
    cursor: pointer;
    font-size: 0.8rem;
    padding: 4px 8px;
    border-radius: 20px;
    margin-bottom: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    opacity: 0.9;
    transition: all 0.2s ease;
    line-height: 1.2;
    display: block;
    max-width: 100%;
    position: relative;
}

.calendar-circuit:hover {
    opacity: 1;
    transform: translateX(2px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
}

/* Réserve #134 : afficher le nom complet de l'élément dans les cellules
   (retour à la ligne au lieu de la troncature). Spécificité 0-2-0 pour
   battre la classe Bootstrap .text-truncate et la règle .calendar-circuit.
   pre-line honore les \n issus de circuit.name.replaceAll('-', '\n'). */
.calendar-circuit.calendar-circuit-name {
    white-space: pre-line;
    overflow: visible;
    text-overflow: clip;
    word-break: break-word;
}

/* Conteneur des voitures au survol */
.circuit-cars-container {
    position: absolute;
    top: 100%;
    left: 0;
    width: 220px;
    background-color: white;
    border-radius: 10px;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
    padding: 12px;
    z-index: 100;
    margin-top: 8px;
    border: 2px solid #dee2e6;
    animation: fadeIn 0.3s ease-out;
}

.circuit-car {
    padding: 10px 14px;
    margin-bottom: 8px;
    border-radius: 8px;
    font-size: 0.85rem;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    border: 1px solid transparent;
}

.circuit-car:hover {
    transform: translateX(3px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.5);
}

/* Couleurs */
.bg-primary {
    background-color: #0d6efd !important;
}

.bg-success {
    background-color: #198754 !important;
}

.bg-danger {
    background-color: #dc3545 !important;
}

.bg-warning {
    background-color: #ffc107 !important;
    color: #212529 !important;
}

.bg-info {
    background-color: #0dcaf0 !important;
    color: #212529 !important;
}

.bg-secondary {
    background-color: #6c757d !important;
}

.bg-dark {
    background-color: #212529 !important;
}

/* Détails de la date sélectionnée */
.selected-date-info {
    padding: 20px;
    background: linear-gradient(to right, #f8f9fa, #ffffff);
    border-radius: 12px;
    margin-top: 25px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
    transition: all 0.3s ease;
    border-left: 4px solid #0d6efd;
}

.selected-date-info h3 {
    font-size: 1.3rem;
    color: #343a40;
    font-weight: 600;
    letter-spacing: -0.5px;
    margin-bottom: 1.5rem;
}

.circuits-list .fs-5 {
    font-weight: 600;
    color: #444;
}

.list-group-item {
    border: none;
    border-radius: 10px;
    margin-bottom: 8px;
    padding: 12px 16px;
    transition: all 0.2s ease;
    background-color: white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.list-group-item:hover {
    background-color: #f9f9f9;
    transform: translateX(3px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.cars-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.car-item {
    display: flex;
    align-items: center;
    font-size: 0.85rem;
    padding: 4px 10px;
    background-color: #f8f9fa;
    border-radius: 20px;
}

.car-badge {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-right: 6px;
    display: inline-block;
}

.alert-info {
    background-color: rgba(13, 202, 240, 0.1);
    border: none;
    border-radius: 10px;
    color: #055160;
    padding: 16px;
}

/* Animation pour les transitions entre les mois */
@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Styles responsifs */
@media (max-width: 992px) {
    .calendar-day {
        min-height: 100px;
    }
}

@media (max-width: 768px) {
    .calendar-container {
        padding: 1.5rem;
    }

    .calendar-day {
        min-height: 80px;
    }

    .calendar-date {
        width: 32px;
        height: 32px;
        font-size: 0.9rem;
    }

    .calendar-circuit {
        font-size: 0.7rem;
    }

    .circuit-cars-container {
        width: 180px;
    }
}

@media (max-width: 576px) {
    .calendar-container {
        padding: 15px;
        border-radius: 10px;
        margin: 1rem 1rem 3rem 1rem;
    }

    .calendar-day {
        min-height: 60px;
        margin: 1px;
        border-radius: 8px;
        padding: 1px !important;
    }

    .calendar-date {
        width: 28px;
        height: 28px;
        font-size: 0.7rem;
        border: none;
        box-shadow: none;
        float: right;
    }

    .calendar-day-header {
        font-size: 0.7rem;
        padding: 8px 0;
    }

    .btn-group .btn {
        padding: 0.3rem 0.6rem;
        font-size: 0.85rem;
    }

    .calendar-circuits {
        position: absolute;
        bottom: 0;
        max-width: inherit;
    }

    .calendar-circuit {
        padding: 0.1rem 0.3rem;
        font-size: 0.65rem;
        margin-bottom: 0.2rem;
        border-radius: 12px;
    }

    .circuit-cars-container {
        width: 150px;
    }

    .selected-date-info {
        padding: 1rem;
    }

    .selected-date-info h3 {
        font-size: 1.1rem;
        margin-bottom: 1rem;
    }

    .car-modal-content {
        width: 90%;
        max-width: 300px;
    }
}

/* Styles pour le modal de sélection de voiture */
.car-selection-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1030;
}

.car-modal-backdrop {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(3px);
}

.car-modal-content {
    position: relative;
    width: 90%;
    max-width: 500px;
    background-color: white;
    border-radius: 16px;
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
    overflow: hidden;
    z-index: 1001;
    animation: modalFadeIn 0.3s ease-out;
}

.car-modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid #eee;
}

.car-modal-header h3 {
    margin: 0;
    font-size: 1.3rem;
    font-weight: 600;
    color: #333;
}

.car-modal-close {
    background: none;
    border: none;
    font-size: 1.5rem;
    color: #666;
    cursor: pointer;
    transition: color 0.2s;
}

.car-modal-close:hover {
    color: #333;
}

.car-modal-body {
    padding: 20px;
    max-height: 70vh;
    overflow-y: auto;
}

.car-list h4 {
    margin-top: 0;
    margin-bottom: 16px;
    font-size: 1.1rem;
    color: #444;
}

.car-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 16px;
}

.car-card {
    padding: 16px;
    border-radius: 12px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    transition: all 0.2s ease;
    color: white;
}

.car-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
}

.car-name {
    font-weight: 500;
    font-size: 1rem;
    text-align: center;
}

/* Couleurs pour les cartes de voitures */
.car-danger {
    background-color: #dc3545;
}

.car-warning {
    background-color: #ffc107;
    color: #212529;
}

.car-primary {
    background-color: #0d6efd;
}

.car-info {
    background-color: #0dcaf0;
    color: #212529;
}

.car-secondary {
    background-color: #6c757d;
}

.car-success {
    background-color: #198754;
}

.car-dark {
    background-color: #212529;
}

@keyframes modalFadeIn {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Styles for the spinner backdrop */
.spinner-backdrop {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(255, 255, 255, 0.8);
    z-index: 1002;
}

/* Larger spinner */
.spinner-border-lg {
    width: 3rem;
    height: 3rem;
    border-width: 0.25rem;
}
</style>
